import React from "react";

const krw = (n) => `₩${Math.round(Number(n) || 0).toLocaleString()}`;
const krwSigned = (n) => {
    const v = Number(n) || 0;
    return (v >= 0 ? "+₩" : "-₩") + Math.abs(Math.round(v)).toLocaleString();
};
const pct = (n) => {
    const v = Number(n) || 0;
    return (v >= 0 ? "+" : "") + v.toFixed(2) + "%";
};

/**
 * DeltaMonthlySummary — 델타전략 월별 손익 요약 (DAS-179, History 에서 이동).
 *
 * 값은 `utils/deltaHistory.buildDeltaMonthlySummary` 가 만든다 — BTC 제외 규칙이 그 한 곳에만 있다.
 * 스냅샷이 없어 BTC 를 빼지 못한 달에는 `BTC 포함` 표식을 단다. 손익금이 BTC 등락에 오염됐다는 뜻이라
 * 그 달의 숫자를 다른 달과 나란히 비교하면 안 된다.
 *
 * 모바일은 5열 표가 들어가지 않으므로 카드 스택으로 바꾼다 (DAS-178 에서 확인한 폭 제약).
 */
function BtcTag() {
    return (
        <span
            className="rounded-full bg-warning-subtle px-2 py-0.5 text-xs font-medium text-warning"
            title="이 달에는 BTC 적립분을 뺄 수 없는 구간이 있습니다 — 손익이 BTC 등락에 영향을 받습니다"
        >
            BTC 포함
        </span>
    );
}

function DeltaMonthlySummary({ rows }) {
    const list = Array.isArray(rows) ? rows : [];

    return (
        <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card">
            <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-content-secondary">월별 손익 요약</h3>
                <div className="font-num text-xs text-content-muted">{list.length}개월</div>
            </div>

            {list.length === 0 ? (
                <div className="mt-6 py-8 text-center text-xs text-content-muted">
                    아직 집계할 월이 없습니다.
                </div>
            ) : (
                <>
                    {/* 데스크톱: 표 */}
                    <div className="mt-4 hidden overflow-x-auto sm:block">
                        <table className="w-full border-collapse text-sm">
                            <thead>
                                <tr className="text-xs text-content-muted">
                                    <th className="w-28 px-3 py-2 text-left font-medium">월</th>
                                    <th className="px-3 py-2 text-right font-medium">시작 잔고</th>
                                    <th className="px-3 py-2 text-right font-medium">마감 잔고</th>
                                    <th className="px-3 py-2 text-right font-medium">손익금</th>
                                    <th className="w-24 px-3 py-2 text-right font-medium">손익률</th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.map((r) => {
                                    const tone = r.profitLoss >= 0 ? "text-gain" : "text-loss";
                                    return (
                                        <tr key={r.month} className="border-t border-surface-border">
                                            <td className="px-3 py-2.5 text-left">
                                                <span className="font-num text-content">{r.month}</span>
                                                {r.btcIncluded && <span className="ml-1.5"><BtcTag /></span>}
                                            </td>
                                            <td className="px-3 py-2.5 text-right font-num text-content-secondary">{krw(r.startTotal)}</td>
                                            <td className="px-3 py-2.5 text-right font-num text-content">{krw(r.endTotal)}</td>
                                            <td className={`px-3 py-2.5 text-right font-num font-semibold ${tone}`}>{krwSigned(r.profitLoss)}</td>
                                            <td className={`px-3 py-2.5 text-right font-num font-semibold ${tone}`}>{pct(r.profitLossRate)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* 모바일: 카드 스택 — 5열 표는 390px 에 들어가지 않는다 (DAS-178) */}
                    <div className="mt-4 flex flex-col gap-2 sm:hidden">
                        {list.map((r) => {
                            const tone = r.profitLoss >= 0 ? "text-gain" : "text-loss";
                            return (
                                <div key={r.month} className="rounded-xl border border-surface-border bg-surface-card-alt p-3">
                                    <div className="flex flex-wrap items-center justify-between gap-1.5">
                                        <div className="flex items-center gap-1.5">
                                            <span className="font-num text-xs font-semibold text-content">{r.month}</span>
                                            {r.btcIncluded && <BtcTag />}
                                        </div>
                                        <span className={`font-num text-xs font-semibold ${tone}`}>{pct(r.profitLossRate)}</span>
                                    </div>
                                    <div className="mt-2 flex items-baseline justify-between gap-2">
                                        <span className={`font-num text-base font-semibold ${tone}`}>{krwSigned(r.profitLoss)}</span>
                                        <span className="font-num text-xs text-content-muted">→ {krw(r.endTotal)}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}

export default DeltaMonthlySummary;
